"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { usePostHog } from "posthog-js/react";
import { useReducedMotion } from "@/lib/motion";
import { usePrefersLight } from "@/hooks/use-color-scheme";
import { buildBorderPolylines } from "@/lib/globe-borders";
import { COUNTRY_BY_ID } from "@/lib/globe-features";
import { cn } from "@/lib/utils";
import { invert, makeCamera, rotForLon, tiltForLat } from "./projection";
import { countryAt } from "./hit-test";
import {
  drawAtmosphere,
  drawBorders,
  drawMarker,
  drawRim,
  fillCountry,
  globeGreen,
  setGlobeTheme,
} from "./draw";
import type { LocationsGlobeProps } from "./types";

// Built once: the faint background border map, shared with the home globe's look.
const BORDERS = buildBorderPolylines();
const AUTO_SPIN = 0.004;

export default function LocationsGlobe({
  mode,
  highlightedCountryIds,
  countryHrefById,
  focusCountryId,
  cityMarker,
  interactive = true,
  onCountrySelect,
  radiusFactor = 0.44,
  className,
}: LocationsGlobeProps) {
  const router = useRouter();
  const posthog = usePostHog();
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const tipRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef(0);

  const [size, setSize] = useState(0);
  const [hovered, setHovered] = useState<{ id: string; name: string } | null>(null);

  const isFocus = mode === "focus";
  const highlightedSet = useMemo(
    () => new Set(highlightedCountryIds ?? []),
    [highlightedCountryIds]
  );
  const focusFeature = useMemo(
    () => (focusCountryId ? COUNTRY_BY_ID.get(focusCountryId) : undefined),
    [focusCountryId]
  );

  // Available-country links for the no-canvas / screen-reader fallback (explore mode).
  const fallbackLinks = useMemo(() => {
    if (isFocus || !countryHrefById) return [];
    return Object.entries(countryHrefById)
      .map(([id, href]) => ({ href, name: COUNTRY_BY_ID.get(id)?.name ?? id }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [isFocus, countryHrefById]);

  // Loop state in refs so prop reads don't restart the rAF loop.
  const S = useRef({ rot: 0, tilt: 0, targetRot: 0, targetTilt: 0, animating: false });
  const isDragging = useRef(false);
  const didDrag = useRef(false);
  const lastPtrX = useRef(0);
  const momentum = useRef(0);
  const mouse = useRef<{ x: number; y: number } | null>(null);
  const hoveredIdRef = useRef<string | null>(null);
  const reducedRef = useRef(false);
  const loopRunning = useRef(false);
  const startLoop = useRef<() => void>(() => {});
  const cityMarkerRef = useRef(cityMarker);
  useEffect(() => {
    cityMarkerRef.current = cityMarker;
  }, [cityMarker]);

  const reduced = useReducedMotion();
  useEffect(() => {
    reducedRef.current = !!reduced;
  }, [reduced]);

  const prefersLight = usePrefersLight();
  useEffect(() => {
    setGlobeTheme(prefersLight);
    // Repaint with the new palette even when the loop is parked.
    startLoop.current();
  }, [prefersLight]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => setSize(Math.floor(e.contentRect.width)));
    ro.observe(el);
    if (el.offsetWidth > 0) setSize(el.offsetWidth);
    return () => ro.disconnect();
  }, []);

  // Point the camera at the focused country (jump when reduced motion, else ease in).
  useEffect(() => {
    const s = S.current;
    if (isFocus && focusFeature) {
      s.targetRot = rotForLon(focusFeature.centroidLonLat[0]);
      s.targetTilt = tiltForLat(focusFeature.centroidLonLat[1]);
      if (reducedRef.current) {
        s.rot = s.targetRot;
        s.tilt = s.targetTilt;
        s.animating = false;
      } else {
        s.animating = true;
        startLoop.current();
      }
    } else {
      s.targetRot = 0;
      s.targetTilt = 0;
      s.animating = false;
    }
  }, [isFocus, focusFeature]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || size === 0) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    ctx.scale(dpr, dpr);
    cancelAnimationFrame(rafRef.current);

    const cx = size / 2;
    const cy = size / 2;
    const rG = size * radiusFactor;

    const loop = () => {
      loopRunning.current = true;
      const s = S.current;
      const rm = reducedRef.current;

      if (isFocus) {
        if (s.animating) {
          const dr = s.targetRot - s.rot;
          const dt = s.targetTilt - s.tilt;
          if (Math.abs(dr) < 0.002 && Math.abs(dt) < 0.002) {
            s.rot = s.targetRot;
            s.tilt = s.targetTilt;
            s.animating = false;
          } else {
            s.rot += dr * 0.08;
            s.tilt += dt * 0.08;
          }
        }
      } else if (!isDragging.current && !rm) {
        momentum.current *= 0.92;
        s.rot += AUTO_SPIN + momentum.current;
      }

      const cam = makeCamera(cx, cy, rG, s.rot, isFocus ? s.tilt : 0);

      ctx.clearRect(0, 0, size, size);
      drawAtmosphere(ctx, cam);
      drawRim(ctx, cam);
      drawBorders(ctx, cam, BORDERS);

      // Hover hit-test (explore only), once per frame.
      if (!isFocus && interactive && mouse.current && !isDragging.current) {
        const ll = invert(mouse.current.x, mouse.current.y, cam);
        const hit = ll ? countryAt(ll.lon, ll.lat) : null;
        const id = hit?.id ?? null;
        if (id !== hoveredIdRef.current) {
          hoveredIdRef.current = id;
          setHovered(hit ? { id: hit.id, name: hit.name } : null);
        }
      }

      // Country highlights.
      if (isFocus && focusFeature) {
        // Country/city pages: fill the focused country green.
        fillCountry(ctx, cam, focusFeature, {
          fill: globeGreen(),
          fillAlpha: 0.22,
          strokeAlpha: 0.8,
        });
      } else {
        // Hub: mark each available country with a subtle green dot (home-globe style); only the
        // hovered country gets a gentle fill. Keeps the globe understated, not blanketed in green.
        for (const id of highlightedSet) {
          const f = COUNTRY_BY_ID.get(id);
          if (!f) continue;
          if (hoveredIdRef.current === id) {
            fillCountry(ctx, cam, f, { fill: globeGreen(), fillAlpha: 0.16, strokeAlpha: 0.65 });
          }
          drawMarker(ctx, cam, f.centroidLonLat[0], f.centroidLonLat[1], {
            glowR: 9,
            dotR: 1.8,
            glowAlpha: 0.4,
          });
        }
      }

      if (isFocus && cityMarkerRef.current) {
        drawMarker(ctx, cam, cityMarkerRef.current.lon, cityMarkerRef.current.lat);
      }

      // Position the hover tooltip near the cursor.
      if (tipRef.current && mouse.current && hoveredIdRef.current) {
        const tx = Math.min(mouse.current.x + 16, size - 8);
        const ty = Math.max(mouse.current.y - 10, 8);
        tipRef.current.style.transform = `translate(${tx}px, ${ty}px)`;
        tipRef.current.style.opacity = "1";
      }

      // Park when nothing is animating (reduced-motion explore, or a settled focus globe).
      const settledFocus = isFocus && !s.animating && !isDragging.current;
      const exploreParked = !isFocus && rm && !isDragging.current;
      if (settledFocus || exploreParked) {
        loopRunning.current = false;
        return;
      }
      rafRef.current = requestAnimationFrame(loop);
    };

    startLoop.current = () => {
      if (!loopRunning.current) loop();
    };
    loop();
    return () => {
      cancelAnimationFrame(rafRef.current);
      loopRunning.current = false;
    };
  }, [size, isFocus, focusFeature, highlightedSet, interactive, radiusFactor]);

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (!interactive) return;
      isDragging.current = true;
      didDrag.current = false;
      lastPtrX.current = e.clientX;
      momentum.current = 0;
      S.current.animating = false;
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
      startLoop.current();
    },
    [interactive]
  );

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (rect) mouse.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    if (isDragging.current) {
      const delta = e.clientX - lastPtrX.current;
      if (Math.abs(delta) > 2) didDrag.current = true;
      // Content follows the finger (matches the mirror-corrected projection).
      S.current.rot += delta * 0.007;
      momentum.current = delta * 0.007 * 0.75;
      lastPtrX.current = e.clientX;
      if (hoveredIdRef.current) {
        hoveredIdRef.current = null;
        setHovered(null);
      }
    } else {
      startLoop.current(); // keep hover responsive even when the loop is parked
    }
  }, []);

  const onPointerUp = useCallback(() => {
    isDragging.current = false;
  }, []);

  const onPointerLeave = useCallback(() => {
    isDragging.current = false;
    mouse.current = null;
    if (hoveredIdRef.current) {
      hoveredIdRef.current = null;
      setHovered(null);
    }
  }, []);

  const onClick = useCallback(() => {
    if (isFocus || !interactive || didDrag.current || !mouse.current) return;
    const cam = makeCamera(size / 2, size / 2, size * radiusFactor, S.current.rot, 0);
    const ll = invert(mouse.current.x, mouse.current.y, cam);
    if (!ll) return;
    const hit = countryAt(ll.lon, ll.lat, highlightedSet);
    if (!hit) return;
    const href = countryHrefById?.[hit.id];
    if (!href) return;
    posthog?.capture("location_globe_country_clicked", {
      country_id: hit.id,
      country_name: hit.name,
    });
    onCountrySelect?.(hit.id, hit.name);
    router.push(href);
  }, [
    isFocus,
    interactive,
    size,
    radiusFactor,
    highlightedSet,
    countryHrefById,
    onCountrySelect,
    posthog,
    router,
  ]);

  const hoveredClickable = hovered && countryHrefById?.[hovered.id];

  return (
    <div className={cn("relative w-full", className)}>
      <div
        ref={containerRef}
        className={cn(
          "relative aspect-square w-full min-w-0 select-none",
          interactive &&
            (hoveredClickable ? "cursor-pointer" : "cursor-grab active:cursor-grabbing")
        )}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerLeave={onPointerLeave}
        onClick={onClick}
        aria-label={
          isFocus
            ? "Globe focused on the selected location"
            : "Interactive globe of available proxy locations — drag to rotate, hover a country for its name"
        }
      >
        <canvas
          ref={canvasRef}
          style={{ width: size, height: size, display: "block" }}
          width={size}
          height={size}
          aria-hidden="true"
        />

        <div
          className="pointer-events-none absolute inset-0 z-10"
          style={{ background: "radial-gradient(circle, transparent 62%, var(--background) 80%)" }}
          aria-hidden="true"
        />

        {/* Edge fades — dissolve the atmosphere glow into the background on all sides (matches the home globe). */}
        <div
          className="pointer-events-none absolute inset-x-0 top-0 z-20 h-[18%]"
          style={{
            background: "linear-gradient(to bottom, var(--background) 0%, transparent 100%)",
          }}
          aria-hidden="true"
        />
        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 z-20 h-[18%]"
          style={{ background: "linear-gradient(to top, var(--background) 0%, transparent 100%)" }}
          aria-hidden="true"
        />
        <div
          className="pointer-events-none absolute inset-y-0 left-0 z-20 w-[18%]"
          style={{
            background: "linear-gradient(to right, var(--background) 0%, transparent 100%)",
          }}
          aria-hidden="true"
        />
        <div
          className="pointer-events-none absolute inset-y-0 right-0 z-20 w-[18%]"
          style={{ background: "linear-gradient(to left, var(--background) 0%, transparent 100%)" }}
          aria-hidden="true"
        />

        {hovered && (
          <div
            ref={tipRef}
            aria-live="polite"
            className="border-border bg-background/95 text-foreground pointer-events-none absolute top-0 left-0 z-40 rounded-lg border px-2.5 py-1.5 text-sm font-semibold opacity-0 shadow-xl backdrop-blur-md"
            style={{ willChange: "transform" }}
          >
            {hovered.name}
          </div>
        )}

        {!isFocus && interactive && (
          <div
            className="text-foreground/80 pointer-events-none absolute bottom-3 left-1/2 z-30 flex -translate-x-1/2 items-center gap-1.5 text-xs font-medium select-none"
            aria-hidden="true"
          >
            Drag to rotate
          </div>
        )}
      </div>

      {/* Non-canvas fallback: real, crawlable, keyboard-navigable links to every available location. */}
      {fallbackLinks.length > 0 && (
        <nav className="sr-only" aria-label="Available proxy locations">
          <ul>
            {fallbackLinks.map((l) => (
              <li key={l.href}>
                <a href={l.href}>{l.name}</a>
              </li>
            ))}
          </ul>
        </nav>
      )}
    </div>
  );
}
